
import { useState } from 'react';
import { DocumentUploadModal } from './DocumentUploadModal';
import AssessmentDrawer from '@/components/project/AssessmentDrawer';
import { CertificationSummaryCard } from './CertificationSummaryCard';
import { DocumentCard } from './DocumentCard';

interface CertificationDocumentsProps {
  productId: string;
  certification: string;
}

interface RequiredDocument {
  id: string;
  title: string;
  description: string;
  required: boolean;
  sampleUrl?: string;
  templateUrl?: string;
  uploadedDocs: {
    id: string;
    name: string;
    uploadedAt: string;
    relevanceScore?: number;
    isAnalyzing?: boolean;
    assessment?: string;
    citations?: string;
  }[];
}

export const CertificationDocuments = ({ productId, certification }: CertificationDocumentsProps) => {
  const [selectedDocType, setSelectedDocType] = useState<RequiredDocument | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<RequiredDocument['uploadedDocs'][0] | null>(null);
  const [isAssessmentOpen, setIsAssessmentOpen] = useState(false);

  // Mock required documents data - this would come from the certification system
  const requiredDocuments: RequiredDocument[] = [
    {
      id: 'product-info',
      title: 'Product Information Sheet',
      description: 'Complete product specifications including ingredients, performance characteristics, and intended use.',
      required: true,
      sampleUrl: '/samples/product-info-sample.pdf',
      templateUrl: '/templates/product-info-template.pdf',
      uploadedDocs: [
        { 
          id: '1', 
          name: 'EcoClean_Product_Info_v1.pdf', 
          uploadedAt: '2024-01-10',
          relevanceScore: 92,
          assessment: `The document provides comprehensive product information for EcoClean All-Purpose Cleaner, demonstrating full compliance with Green Seal certification requirements. The product specification sheet includes detailed ingredient disclosure, performance characteristics, and environmental impact data.

The formulation analysis shows the product contains 95% bio-based ingredients, exceeding the minimum 90% requirement for Green Seal GS-37 standard. All ingredients are disclosed with proper CAS numbers and safety classifications. The product demonstrates effective cleaning performance while maintaining low environmental impact through biodegradable surfactants and renewable raw materials.

The documentation includes third-party laboratory testing results confirming cleaning efficacy, aquatic toxicity assessments, and biodegradability studies. All test methods align with EPA and ASTM standards referenced in the Green Seal criteria.`,
          citations: `GS37_Product_Information_Template.pdf
EcoClean_Ingredient_Disclosure_v2.1.pdf
Third_Party_Testing_Results_Q1_2024.pdf

Source: Document Analysis`
        }
      ]
    },
    {
      id: 'ingredient-disclosure',
      title: 'Ingredient Disclosure',
      description: 'Full ingredient list with CAS numbers, percentages, and safety data sheets for each component.',
      required: true,
      sampleUrl: '/samples/ingredient-disclosure-sample.pdf',
      templateUrl: '/templates/ingredient-disclosure-template.xlsx',
      uploadedDocs: []
    },
    {
      id: 'performance-testing',
      title: 'Performance Testing Results',
      description: 'Third-party laboratory test results demonstrating product efficacy according to relevant standards.',
      required: true,
      sampleUrl: '/samples/performance-testing-sample.pdf',
      uploadedDocs: [
        { 
          id: '2', 
          name: 'Lab_Test_Results_Q1_2024.pdf', 
          uploadedAt: '2024-01-08',
          isAnalyzing: true
        },
        { 
          id: '3', 
          name: 'Efficacy_Study_Final.pdf', 
          uploadedAt: '2024-01-05',
          relevanceScore: 78,
          assessment: `The efficacy study provides thorough testing results for cleaning performance across multiple surface types and soil conditions. The testing protocol follows ASTM D4488 methodology with statistically significant results showing 99.2% removal efficiency for standard test soils.

The study includes comprehensive testing on various surfaces including glass, stainless steel, ceramic tile, and laminate flooring. Results demonstrate consistent performance across all tested surfaces with minimal variability. The product shows particular strength in grease and protein-based soil removal.

Additional testing covers dilution ratios, contact time requirements, and compatibility with common cleaning tools. The results support the manufacturer's usage claims and provide data for proper application guidelines.`,
          citations: `ASTM_D4488_Test_Protocol.pdf
Efficacy_Study_Complete_Dataset.xlsx
Surface_Compatibility_Testing.pdf

Source: Laboratory Analysis`
        }
      ]
    },
    {
      id: 'packaging-info',
      title: 'Packaging Information',
      description: 'Details about packaging materials, recyclability, and environmental impact.',
      required: true,
      templateUrl: '/templates/packaging-info-template.pdf',
      uploadedDocs: []
    },
    {
      id: 'manufacturing-process',
      title: 'Manufacturing Process Documentation',
      description: 'Detailed description of manufacturing processes, quality control measures, and facility certifications.',
      required: false,
      sampleUrl: '/samples/manufacturing-sample.pdf',
      uploadedDocs: []
    }
  ];

  const handleUpload = (docType: RequiredDocument) => {
    setSelectedDocType(docType);
    setIsUploadModalOpen(true);
  };

  const handleDownloadSample = (url: string) => {
    // In a real app, this would trigger a download
    console.log('Downloading sample:', url);
  };

  const handleDownloadTemplate = (url: string) => {
    // In a real app, this would trigger a download
    console.log('Downloading template:', url);
  };

  const handleViewAssessment = (doc: RequiredDocument['uploadedDocs'][0]) => {
    setSelectedDocument(doc);
    setIsAssessmentOpen(true);
  };

  const getTotalUploaded = () => {
    return requiredDocuments.reduce((total, doc) => total + doc.uploadedDocs.length, 0);
  };

  const getRequiredCount = () => {
    return requiredDocuments.filter(doc => doc.required).length;
  };

  const getCompletedRequired = () => {
    return requiredDocuments.filter(doc => doc.required && doc.uploadedDocs.length > 0).length;
  };

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <CertificationSummaryCard
        totalUploaded={getTotalUploaded()}
        completedRequired={getCompletedRequired()}
        requiredCount={getRequiredCount()}
      />

      {/* Documents List */}
      <div className="space-y-4">
        {requiredDocuments.map((doc) => (
          <DocumentCard
            key={doc.id}
            document={doc}
            onUpload={handleUpload}
            onDownloadSample={handleDownloadSample}
            onDownloadTemplate={handleDownloadTemplate}
            onViewAssessment={handleViewAssessment}
          />
        ))}
      </div>

      {/* Upload Modal */}
      <DocumentUploadModal 
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        documentType={selectedDocType}
        productId={productId}
        onUploadSuccess={() => {
          // Refresh the documents list
          setIsUploadModalOpen(false);
        }}
      />

      {/* Assessment Drawer */}
      <AssessmentDrawer 
        open={isAssessmentOpen}
        onOpenChange={setIsAssessmentOpen}
        assessment={selectedDocument?.assessment || null}
        citations={selectedDocument?.citations || null}
        fileName={selectedDocument?.name || ''}
      />
    </div>
  );
};
